import { useState, useEffect, SetStateAction, Dispatch, useMemo } from "react";
import styled, { css } from "styled-components";
import _ from "lodash";
import {
    fetchAllLocations,
    selectLocationSearch,
    useAppDispatch,
    useAppSelector,
} from "@redux";
import { ILocation, LocationSelections, sectionConfigs, SectionKey } from "@interfaces";
import { deriveLocationLists, theme } from "@utilities";

interface LocationPickerProps {
    setSelections: Dispatch<SetStateAction<LocationSelections>>;
    selections: LocationSelections;
}

export const LocationPicker = ({ setSelections, selections }: LocationPickerProps) => {
    const dispatch = useAppDispatch();
    const searchState = useAppSelector(selectLocationSearch);
    const [expanded, setExpanded] = useState<SectionKey | null>(null);

    // Single FT call on mount populates the entire tree. After this, every
    // level of the picker reads from the cached tree with zero further
    // network requests.
    useEffect(() => {
        if (
            _.isEmpty(searchState.allLocations) &&
            !searchState.isLoadingAllLocations
        ) {
            dispatch(fetchAllLocations());
        }
    }, [dispatch, searchState.allLocations, searchState.isLoadingAllLocations]);

    const { countries, states, regions, localities, hasStatesLayer } = useMemo(
        () =>
            deriveLocationLists(searchState.allLocations, {
                countryId: selections.Country?.id,
                stateId: selections.State?.id,
                regionId: selections.Region?.id,
            }),
        [
            searchState.allLocations,
            selections.Country?.id,
            selections.State?.id,
            selections.Region?.id,
        ],
    );

    const listsBySection: Record<SectionKey, ILocation[]> = {
        Country: countries,
        State: states,
        Region: regions,
        Locality: localities,
    };

    const toggle = (section: SectionKey) =>
        setExpanded((prev) => (prev === section ? null : section));

    const handleSelect = (section: SectionKey, item: ILocation | null) => {
        const config = sectionConfigs.find((sc) => sc.key === section)!;
        const resetValues = _.fromPairs(_.map(config.resetAfter, (k) => [k, null]));
        setSelections((prev) => ({
            ...prev,
            [section]: item,
            ...resetValues,
        }));
    };

    // Skip the State row entirely when the selected country has no
    // states layer (e.g. Cambodia, Costa Rica). The reset cascade still
    // works — Region's resetAfter only includes Locality, so jumping
    // directly from Country to Region is safe.
    const visibleSections = _.filter(sectionConfigs, ({ key }) => {
        if (key === "State" && selections.Country && !hasStatesLayer) return false;
        return true;
    });

    const isLoading = searchState.isLoadingAllLocations;
    const error = searchState.allLocationsError;

    return (
        <PickerWrapper>
            <Body>
                {_.map(visibleSections, ({ key }) => {
                    const items: ILocation[] = listsBySection[key] ?? [];

                    // build displayItems with “Any” for those three sections
                    const withAny = _.includes(["State", "Region", "Locality"], key)
                        ? _.concat([{ id: "", text: "Any", isAny: true }], items)
                        : items;
                    const displayItems = _.filter(withAny, (it) =>
                        it.isAny
                            ? true
                            : !_.includes(it.text, "- To be Confirmed") &&
                              !_.includes(it.text, "No Results"),
                    );

                    // if nothing selected, defaultText is “Any” for State/Region/Locality, else blank
                    const defaultText = _.includes(["State", "Region", "Locality"], key)
                        ? "Any"
                        : "";
                    // get the actual display label: either the selected item's .text, or defaultText
                    const selectedText = _.get(selections, [key, "text"], defaultText);

                    return (
                        <div key={key}>
                            <SectionHeader onClick={() => toggle(key)}>
                                <HeaderLabel>
                                    <PinIcon />
                                    {`Choose ${key}`}
                                </HeaderLabel>
                                <Chevron
                                    style={{
                                        transform:
                                            expanded === key ? "rotate(180deg)" : "rotate(0deg)",
                                    }}
                                />
                            </SectionHeader>

                            {/* show “Any” by default when nothing is picked */}
                            <SectionSelected onClick={() => toggle(key)}>
                                {selectedText}
                            </SectionSelected>

                            {expanded === key && (
                                <Options>
                                    {isLoading ? (
                                        <LoadingText>Loading {_.toLower(key)}…</LoadingText>
                                    ) : error ? (
                                        <LoadingText>Error loading {_.toLower(key)}</LoadingText>
                                    ) : _.isEmpty(displayItems) ? null : (
                                        _.map(displayItems, (item) => {
                                            const isAny = Boolean((item as any).isAny);
                                            // “Any” is selected when selections[key] is null
                                            const selected = isAny
                                                ? _.isNil(selections[key])
                                                : selections[key]?.id === item.id;

                                            return (
                                                <OptionRow
                                                    key={isAny ? "any" : item.id}
                                                    onClick={() =>
                                                        handleSelect(
                                                            key,
                                                            isAny ? null : (item as ILocation),
                                                        )
                                                    }
                                                >
                                                    <RadioCircle selected={selected} />
                                                    <OptionLabel selected={selected}>
                                                        {item.text}
                                                    </OptionLabel>
                                                </OptionRow>
                                            );
                                        })
                                    )}
                                </Options>
                            )}
                        </div>
                    );
                })}
            </Body>
        </PickerWrapper>
    );
};

/** Styled Components **/

const PickerWrapper = styled.div`
    display: flex;
    flex-direction: column;
    height: auto;
`;

const Body = styled.div`
    overflow-y: auto;
    padding: 18px;
    max-height: 100vh-20px;
`;

const SectionHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    cursor: pointer;
    padding-top: 20px;
`;

const SectionSelected = styled.div`
    border-bottom: 1px solid #e0e0e0;
    padding: 5px 0 10px 40px;
    /* A literal rather than a token: no palette grey matches, and the nearest
       (gray_3, #828282) is a different shade. Same value as the active
       breadcrumb. */
    color: #747474;
`;

const HeaderLabel = styled.span`
    display: flex;
    align-items: center;
    font-size: 16px;
    font-weight: 700;
    color: #333;
    gap: 20px;
`;

const PinIcon = () => <img src="/images/fi_map-pin.png" alt="pin" width={20} height={20} />;

const Chevron = styled.img.attrs({
    src: "/images/fi_chevron-down.png",
    alt: "chevron",
})`
    width: 20px;
    height: 20px;
    transition: transform 0.2s;
`;

const Options = styled.div`
    padding: 8px 0 0 32px;
`;

const OptionRow = styled.div`
    display: flex;
    align-items: center;
    position: relative;
    gap: 16px;
    padding: 8px 0;
    cursor: pointer;
`;

const RadioCircle = styled.div<{ selected: boolean }>`
    width: 24px;
    height: 24px;
    border-radius: 50%;
    border: 1px solid ${({ selected }) => (selected ? theme.colors.time_radio : "#daddde")};
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: background-color 0.3s ease;

    ${({ selected }) =>
        selected &&
        css`
            background-color: ${theme.colors.time_radio};

            &::after {
                content: "";
                width: 10px;
                height: 10px;
                background-color: #fff;
                border-radius: 50%;
            }
        `}
`;

const OptionLabel = styled.span<{ selected: boolean }>`
    flex-grow: 1;
    font-size: 16px;
    color: ${({ selected }) => (selected ? theme.colors.label : "#555")};
`;

const LoadingText = styled.div`
    padding: 8px 0 0px 8px;
    color: #888;
`;
